// src/pages/admin/users/EditUserPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/api";
import { buildPublicFileUrl } from "../../../config/env";
import {
  getPlatformTenant,
  getSelectedPlatformTenant,
} from "../../../services/platformTenantsApi";
import { fetchRoleOptions } from "../../../services/applicationRolesApi";
import {
  evaluateLicenseProvisioning,
  formatLicenseSummary,
  formatLicenseWindow,
  licenseStatusColor,
  licenseStatusLabel,
} from "../../../utils/tenantLicense";

/* -------------------- MUI -------------------- */
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";

/* -------------------- CONST ------------------ */
const DEFAULT_APPRAISAL_CATEGORY = "line_employee";
const FALLBACK_ROLE_OPTIONS = [
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "hr_admin", label: "HR Admin" },
  { value: "finance", label: "Finance" },
  { value: "finance_coordinator", label: "Finance Coordinator" },
  { value: "ceo", label: "CEO" },
];

const emptyUser = {
  employee_code: "",
  name: "",
  email: "",
  iqama_no: "",
  passport_no: "",
  passport_issue_date: "",
  passport_expiry_date: "",
  position: "",
  department: "",
  cost_center: "",
  site: "",
  nationality: "",
  birth_date: "",
  contract_type: "",
  contract_issue_date: "",
  contract_expiry_date: "",
  password: "",

  // system role
  role: "employee",

  // appraisal category
  actual_role: DEFAULT_APPRAISAL_CATEGORY,

  // ✅ workflow override dropdown state: "" | number
  performance_workflow_template_id: "",

  project_id: "", // "" | number
  manager_id: "",

  bank_name: "",
  iban: "",
  account_number: "",

  insurance_policy_id: "",
  insurance_classification: "",
  start_date: "",
  end_date: "",
};

const emptyRelative = {
  name: "",
  relationship: "",
  birth_date: "",
  insurance_policy_id: "",
  insurance_classification: "",
};

const emptyCertificate = {
  title: "",
  issue_date: "",
  certificate_file: null,
};

const relationshipOptions = [
  "Spouse",
  "Son",
  "Daughter",
  "Father",
  "Mother",
  "Brother",
  "Sister",
  "Other",
];

/* -------------------- HELPERS ------------------ */
const toText = (v) => (v === null || v === undefined ? "" : String(v));
const toNumOrEmpty = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
};

const formatAxiosError = (err, fallback = "Save failed") => {
  const msg = err?.response?.data?.message || err?.message || fallback;
  const errors = err?.response?.data?.errors;
  if (!errors) return msg;

  const details = Object.entries(errors)
    .map(([k, arr]) => `${k}: ${Array.isArray(arr) ? arr.join(", ") : String(arr)}`)
    .join(" | ");

  return details ? `${msg} — ${details}` : msg;
};

/* -------------------- STYLED ------------------ */
const StickySaveButton = styled(Button)(({ theme }) => ({
  position: "sticky",
  bottom: theme.spacing(2),
  marginLeft: "auto",
  zIndex: 10,
}));

const StatusChip = ({ status }) => (
  <Chip
    label={status}
    color={status === "assigned" ? "success" : "warning"}
    size="small"
    variant="outlined"
  />
);

const SectionTitle = ({ children }) => (
  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
    {children}
  </Typography>
);

const FormSectionCard = ({ title, subtitle, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 2, md: 2.5 },
      borderRadius: 3,
      borderColor: "divider",
      background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
      {title}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Paper>
);

const SummaryPill = ({ label, value }) => (
  <Paper
    variant="outlined"
    sx={{
      px: 1.5,
      py: 1.25,
      borderRadius: 2.5,
      minWidth: 140,
      backgroundColor: "rgba(255,255,255,0.08)",
      borderColor: "rgba(255,255,255,0.18)",
      backdropFilter: "blur(8px)",
    }}
  >
    <Typography
      variant="caption"
      sx={{ display: "block", color: "rgba(255,255,255,0.72)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: "#fff", fontWeight: 800, mt: 0.35 }}>
      {value || "—"}
    </Typography>
  </Paper>
);

/* ------------------ COMPONENT ----------------- */
export default function EditUserPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isPlatformAdmin = localStorage.getItem("is_platform_admin") === "1";
  const selectedPlatformTenant = getSelectedPlatformTenant();
  const hasPlatformTenantSelection = Boolean(selectedPlatformTenant?.key);

  /* -------------------- STATES -------------------- */
  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState(emptyUser);
  const [managers, setManagers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);

  // ✅ workflow templates for override dropdown
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [roleOptions, setRoleOptions] = useState(FALLBACK_ROLE_OPTIONS);

  const [familyMembers, setFamilyMembers] = useState([]);
  const [newFamilyMember, setNewFamilyMember] = useState(emptyRelative);
  const [familyFilter, setFamilyFilter] = useState("");
  const [confirmFm, setConfirmFm] = useState(null);

  const [certificates, setCertificates] = useState([]);
  const [newCertificate, setNewCertificate] = useState(emptyCertificate);
  const [certificateFilter, setCertificateFilter] = useState("");
  const [confirmCert, setConfirmCert] = useState(null);

  const [userAssets, setUserAssets] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [assetToAssign, setAssetToAssign] = useState(null);
  const [confirmAsset, setConfirmAsset] = useState(null);

  const [profileImageFile, setProfileImageFile] = useState(null);
  const [passportImageFile, setPassportImageFile] = useState(null);
  const [currentProfileImageUrl, setCurrentProfileImageUrl] = useState("");
  const [currentPassportImageUrl, setCurrentPassportImageUrl] = useState("");
  const [tenantLicensePayload, setTenantLicensePayload] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [initialLicenseUser, setInitialLicenseUser] = useState(null);

  /* -------------------- APPRAISAL ROLE OPTIONS -------------------- */
  const baseActualRoleOptions = useMemo(
    () => [
      { value: "line_employee", label: "Default – Line Employee (Manager → Employee → Second Level → HR)" },
      { value: "head_of_department", label: "Head of Department (CEO → Executive → HR)" },
      { value: "project_manager", label: "Project Manager (HoD → Operations → HR)" },
      { value: "pmc_project_manager", label: "Project Manager (PMC) (Operations → HR)" },
      { value: "head_office_employee", label: "Head Office Employee (HoD → Operations → HR)" },
      { value: "business_development", label: "Business Development (Operations → CEO → HR)" },
      { value: "operation_manager", label: "Operations Manager (CEO → HR)" },
      { value: "hr_team", label: "HR Administration Team (Direct Manager → CEO → Admin HR)" },
      { value: "employee", label: "Legacy – Employee (old default)" },
      { value: "operation", label: "Legacy – Operation" },
      { value: "operations", label: "Legacy – Operations" },
      { value: "manager", label: "Legacy – Manager" },
      { value: "executive_manager", label: "Legacy – Executive" },
      { value: "hr_admin", label: "Legacy – HR Admin" },
      { value: "admin_hr", label: "Legacy – Admin HR" },
      { value: "ceo", label: "Legacy – CEO / GM" },
    ],
    []
  );

  const actualRoleOptions = useMemo(() => {
    const current = String(formData.actual_role || "").trim();
    if (!current) return baseActualRoleOptions;

    const exists = baseActualRoleOptions.some((o) => o.value === current);
    if (exists) return baseActualRoleOptions;

    return [{ value: current, label: `Current – ${current}` }, ...baseActualRoleOptions];
  }, [baseActualRoleOptions, formData.actual_role]);

  /* -------------------- MEMO / FILTERS -------------------- */
  const managerOptions = useMemo(
    () => managers.filter((m) => String(m.employee_code || "") !== String(formData.employee_code || "")),
    [managers, formData.employee_code]
  );

  const filteredFamily = useMemo(() => {
    const t = familyFilter.toLowerCase();
    if (!t) return familyMembers;
    return familyMembers.filter((f) => (f.name || "").toLowerCase().includes(t));
  }, [familyMembers, familyFilter]);

  const filteredCertificates = useMemo(() => {
    const t = certificateFilter.toLowerCase();
    if (!t) return certificates;
    return certificates.filter((c) => (c.title || "").toLowerCase().includes(t));
  }, [certificates, certificateFilter]);

  /* -------------------- LOAD HELPERS -------------------- */
  const normalizeTemplates = (raw) => {
    const arr = raw?.data?.templates ?? raw?.templates ?? raw?.data?.data ?? raw?.data ?? raw;
    const list = Array.isArray(arr) ? arr : [];
    return list
      .map((t) => ({
        id: t?.id,
        name: t?.name || t?.title || t?.template_name || "",
        ...t,
      }))
      .filter((t) => t?.id != null);
  };

  const fetchWorkflowTemplates = async () => {
    // Only real endpoints — the /list, /all, /index variants don't exist and
    // were 404-ing into the SPA fallback (wasted sequential round-trips).
    const candidates = [
      "/performance/workflow-templates?per_page=1000",
      "/performance/workflow-templates",
    ];

    for (const url of candidates) {
      try {
        const res = await api.get(url);
        const list = normalizeTemplates(res);
        if (list.length) return list;
      } catch {
        // try next
      }
    }
    return [];
  };

  /* -------------------- LOAD DATA ------------- */
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");

      try {
        // Perf: fire everything that does NOT depend on the resolved user in ONE
        // parallel round (was ~6 sequential round-trips). Only assets-for-user
        // needs user.employee_code, so it runs in a small second round.
        const [
          { data: user },
          { data: allUsers },
          { data: pols },
          { data: depts },
          { data: projs },
          { data: assigned },
          { data: fm },
          { data: certs },
          { data: avail },
          templates,
        ] = await Promise.all([
          api.get(`/admin/users/${id}`),
          api.get("/admin/users"),
          api.get("/admin/insurance-policies", { params: { user_id: id } }),
          api.get("/admin/departments"),
          api.get("/projects"),
          api.get(`/admin/users/${id}/assigned-policies`),
          api.get(`/admin/users/${id}/family-members`),
          api.get(`/admin/users/${id}/certificates`),
          api.get("/assets", { params: { status: "available" } }),
          fetchWorkflowTemplates(),
        ]);
        const a = assigned?.[0] || {};

        const { data: userAssts } = await api.get("/assets", { params: { user: user.employee_code } });

        if (cancelled) return;

        // ✅ sanitize: prevent null from overwriting TextField values
        const safe = {
          ...emptyUser,
          ...user,
          employee_code: toText(user?.employee_code),
          name: toText(user?.name),
          email: toText(user?.email),
          iqama_no: toText(user?.iqama_no),
          passport_no: toText(user?.passport_no),
          passport_issue_date: toText(user?.passport_issue_date),
          passport_expiry_date: toText(user?.passport_expiry_date),
          position: toText(user?.position),
          department: toText(user?.department),
          cost_center: toText(user?.cost_center),
          site: toText(user?.site),
          nationality: toText(user?.nationality),
          birth_date: toText(user?.birth_date).slice(0, 10),
          contract_type: toText(user?.contract_type),
          contract_issue_date: toText(user?.contract_issue_date),
          contract_expiry_date: toText(user?.contract_expiry_date),
          bank_name: toText(user?.bank_name),
          iban: toText(user?.iban),
          account_number: toText(user?.account_number),

          role: user?.role ? String(user.role) : "employee",
          actual_role: user?.actual_role ? String(user.actual_role) : DEFAULT_APPRAISAL_CATEGORY,

          // keep state: "" | number
          performance_workflow_template_id: toNumOrEmpty(user?.performance_workflow_template_id),
          project_id: toNumOrEmpty(user?.project_id),
          manager_id: toText(user?.manager_id),

          password: "",

          insurance_policy_id: toText(a.insurance_policy_id || ""),
          insurance_classification: toText(a.classification || ""),
          start_date: toText(a.start_date || ""),
          end_date: toText(a.end_date || ""),
        };

        setFormData(safe);
        setInitialLicenseUser({
          role: user?.role || "employee",
          status: user?.status || "active",
        });
        setCurrentProfileImageUrl(toText(user?.profile_image_url || user?.profile_image_path));
        setCurrentPassportImageUrl(toText(user?.passport_image_url || user?.passport_image_path));
        setProfileImageFile(null);
        setPassportImageFile(null);

        setManagers(allUsers || []);
        setPolicies(pols || []);
        setDepartments(depts || []);
        setProjects(projs || []);
        setFamilyMembers(fm || []);
        setCertificates(certs || []);
        setAvailableAssets(avail || []);
        setUserAssets(userAssts || []);
        setWorkflowTemplates(templates || []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError("Failed to load user.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    fetchRoleOptions()
      .then((payload) => {
        if (cancelled) return;
        const roles = Array.isArray(payload?.roles) ? payload.roles : [];
        if (roles.length) {
          setRoleOptions(roles);
        }
      })
      .catch((err) => console.error("Error loading role options:", err));

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isPlatformAdmin || !hasPlatformTenantSelection || !selectedPlatformTenant?.id) {
      setTenantLicensePayload(null);
      setLicenseLoading(false);
      return;
    }

    let active = true;
    setLicenseLoading(true);

    getPlatformTenant(selectedPlatformTenant.id)
      .then((payload) => {
        if (!active) return;
        setTenantLicensePayload(payload || null);
      })
      .catch(() => {
        if (!active) return;
        setTenantLicensePayload(null);
      })
      .finally(() => {
        if (active) {
          setLicenseLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [hasPlatformTenantSelection, isPlatformAdmin, selectedPlatformTenant?.id]);

  const currentLicense = tenantLicensePayload?.current_license || null;
  const licenseUsage = tenantLicensePayload?.license_usage || null;
  const licenseStatus = tenantLicensePayload?.license_status || "";
  const nextLicense = tenantLicensePayload?.next_license || null;

  const licenseAssessment = useMemo(() => (
    evaluateLicenseProvisioning(tenantLicensePayload, {
      existingUser: initialLicenseUser,
      targetRole: formData.role || initialLicenseUser?.role || "employee",
      targetStatus: initialLicenseUser?.status || "active",
    })
  ), [formData.role, initialLicenseUser, tenantLicensePayload]);

  const saveBlockedReason = useMemo(() => {
    if (!isPlatformAdmin) {
      return "";
    }

    if (!hasPlatformTenantSelection) {
      return "Select a tenant from the super admin dashboard before editing tenant users.";
    }

    if (licenseLoading) {
      return "Current tenant license is still loading.";
    }

    return licenseAssessment.messages[0] || "";
  }, [hasPlatformTenantSelection, isPlatformAdmin, licenseAssessment.messages, licenseLoading]);

  const saveBlocked = Boolean(isPlatformAdmin && saveBlockedReason);

  /* -------------------- HANDLERS -------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // normalize select numeric fields to "" | number
    if (name === "performance_workflow_template_id" || name === "project_id" || name === "insurance_policy_id") {
      setFormData((prev) => ({ ...prev, [name]: value === "" ? "" : Number(value) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value ?? "" }));
  };

  const showSnack = (message) => setSnack(message);

  const handleAddRelative = () => {
    if (!newFamilyMember.name || !newFamilyMember.relationship) return;
    const tempId = `temp-fam-${Date.now()}`;
    setFamilyMembers((prev) => [...prev, { ...newFamilyMember, tempId, _action: "create" }]);
    setNewFamilyMember(emptyRelative);
    showSnack("Family member added (pending Save)");
  };

  const handleBack = () => navigate(-1);
  const handleConfirmDeleteRelative = (fam) => setConfirmFm(fam);

  const handleDeleteRelative = () => {
    if (!confirmFm) return;
    if (confirmFm.tempId) {
      setFamilyMembers((prev) => prev.filter((f) => f.tempId !== confirmFm.tempId));
    } else {
      setFamilyMembers((prev) => prev.map((f) => (f.id === confirmFm.id ? { ...f, _action: "delete" } : f)));
    }
    setConfirmFm(null);
    showSnack("Family member removed (pending Save)");
  };

  const handleAddCertificate = () => {
    if (!newCertificate.title) return;
    const tempId = `temp-cert-${Date.now()}`;
    setCertificates((prev) => [...prev, { ...newCertificate, tempId, _action: "create" }]);
    setNewCertificate(emptyCertificate);
    showSnack("Certificate added (pending Save)");
  };

  const handleConfirmDeleteCertificate = (cert) => setConfirmCert(cert);

  const handleDeleteCertificate = () => {
    if (!confirmCert) return;
    if (confirmCert.tempId) {
      setCertificates((prev) => prev.filter((c) => c.tempId !== confirmCert.tempId));
    } else {
      setCertificates((prev) => prev.map((c) => (c.id === confirmCert.id ? { ...c, _action: "delete" } : c)));
    }
    setConfirmCert(null);
    showSnack("Certificate removed (pending Save)");
  };

  const assignAsset = async () => {
    if (!assetToAssign) return;
    try {
      await api.put(`/assets/${assetToAssign.id}`, {
        user_employee_code: formData.employee_code,
        status: "assigned",
      });
      setUserAssets((prev) => [...prev, { ...assetToAssign, status: "assigned" }]);
      setAvailableAssets((prev) => prev.filter((a) => a.id !== assetToAssign.id));
      setAssetToAssign(null);
      showSnack("Asset assigned");
    } catch (err) {
      console.error(err);
      setError("Could not assign asset");
    }
  };

  const returnAsset = async () => {
    if (!confirmAsset) return;
    try {
      await api.put(`/assets/${confirmAsset.id}`, {
        status: "returned",
        user_employee_code: null,
      });

      setUserAssets((p) => p.filter((a) => a.id !== confirmAsset.id));
      setAvailableAssets((p) => [...p, { ...confirmAsset, status: "returned" }]);

      setConfirmAsset(null);
      showSnack("Asset returned");
    } catch (err) {
      console.error(err);
      setError("Could not return asset");
    }
  };

  /* ========== SAVE EVERYTHING (User, Family, Certificates) ========== */
  const saveAll = async (e) => {
    e.preventDefault();
    setError("");

    if (saveBlockedReason) {
      setError(saveBlockedReason);
      return;
    }

    try {
      const { insurance_policy_id, insurance_classification, start_date, end_date, ...raw } = formData;

      const payload = {
        ...raw,

        actual_role: raw.actual_role || DEFAULT_APPRAISAL_CATEGORY,

        // ✅ critical: send null or number
        performance_workflow_template_id: raw.performance_workflow_template_id === "" ? null : Number(raw.performance_workflow_template_id),

        project_id: raw.project_id === "" ? null : Number(raw.project_id),

        department: raw.department === "" ? null : raw.department,
        manager_id: raw.manager_id === "" ? null : raw.manager_id,

        // ensure text fields never send null
        iqama_no: raw.iqama_no || null,
        passport_no: raw.passport_no || null,
        passport_issue_date: raw.passport_issue_date || null,
        passport_expiry_date: raw.passport_expiry_date || null,
        position: raw.position || null,
        cost_center: raw.cost_center || null,
        site: raw.site || null,
        nationality: raw.nationality || null,
        birth_date: raw.birth_date || null,
        contract_type: raw.contract_type || null,
        contract_issue_date: raw.contract_issue_date || null,
        contract_expiry_date: raw.contract_expiry_date || null,
        bank_name: raw.bank_name || null,
        iban: raw.iban || null,
        account_number: raw.account_number || null,
      };

      if (!payload.password) delete payload.password;

      const hasFiles = Boolean(profileImageFile) || Boolean(passportImageFile);

      const resp = hasFiles
        ? await (() => {
            const fd = new FormData();
            fd.append("_method", "PUT");
            Object.entries(payload).forEach(([key, value]) => {
              if (value === undefined) return;
              fd.append(key, value === null ? "" : String(value));
            });
            if (profileImageFile) fd.append("profile_image", profileImageFile);
            if (passportImageFile) fd.append("passport_image", passportImageFile);

            return api.post(`/admin/users/${id}`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          })()
        : await api.put(`/admin/users/${id}`, payload);

      const updatedUser = resp?.data;
      if (updatedUser) {
        setInitialLicenseUser({
          role: updatedUser?.role || payload.role || initialLicenseUser?.role || "employee",
          status: updatedUser?.status || initialLicenseUser?.status || "active",
        });
        setCurrentProfileImageUrl(toText(updatedUser?.profile_image_url || updatedUser?.profile_image_path));
        setCurrentPassportImageUrl(toText(updatedUser?.passport_image_url || updatedUser?.passport_image_path));
      }
      setProfileImageFile(null);
      setPassportImageFile(null);

      if (insurance_policy_id) {
        await api.post(`/admin/users/${id}/assign-policy`, {
          insurance_policy_id: Number(insurance_policy_id),
          start_date: start_date || null,
          end_date: end_date || null,
          classification: insurance_classification || null,
        });
      }

      await Promise.all(
        familyMembers.map(async (fm) => {
          if (fm._action === "delete") {
            return api.delete(`/admin/users/${id}/family-members/${fm.id}`);
          }
          if (fm._action === "create") {
            const {
              insurance_policy_id: fm_policy_id,
              insurance_classification: fm_class,
              _action,
              tempId,
              ...rest
            } = fm;

            const { data: newFm } = await api.post(`/admin/users/${id}/family-members`, rest);

            if (fm_policy_id) {
              await api.post(`/admin/users/${id}/family-members/${newFm.id}/assign-policy`, {
                insurance_policy_id: Number(fm_policy_id),
                classification: fm_class || null,
              });
            }
          }
          return null;
        })
      );

      await Promise.all(
        certificates.map(async (cert) => {
          if (cert._action === "delete") {
            return api.delete(`/admin/users/${id}/certificates/${cert.id}`);
          }
          if (cert._action === "create") {
            const fd = new FormData();
            fd.append("title", cert.title);
            if (cert.issue_date) fd.append("issue_date", cert.issue_date);
            if (cert.certificate_file) fd.append("certificate_file", cert.certificate_file);

            return api.post(`/admin/users/${id}/certificates`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          }
          return null;
        })
      );

      showSnack("Saved user, family & certificates successfully!");
    } catch (err) {
      console.error(err);
      setError(formatAxiosError(err, "Save failed"));
    }
  };

  /* ------------- RENDER ------------- */
  if (loading) {
    return (
      <Box sx={{ maxWidth: 960, mx: "auto", p: 2 }}>
        <Skeleton variant="text" height={40} width="60%" />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  const selPolicy = policies.find((p) => String(p.id) === String(formData.insurance_policy_id || ""));
  const selFamPolicy = policies.find((p) => String(p.id) === String(newFamilyMember.insurance_policy_id || ""));

  return (
    <Box
      sx={{ maxWidth: 960, mx: "auto", p: 2 }}
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {isPlatformAdmin ? (
        !hasPlatformTenantSelection ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Select a tenant from the super admin dashboard before editing tenant users.
          </Alert>
        ) : (
          <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 3 }}>
            <Stack spacing={1.25}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Tenant License
                </Typography>
                <Chip size="small" color={licenseStatusColor(licenseStatus)} label={licenseStatusLabel(licenseStatus)} />
              </Stack>

              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedPlatformTenant?.name || selectedPlatformTenant?.key}
              </Typography>

              {licenseLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading current license...
                </Typography>
              ) : currentLicense ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {formatLicenseWindow(currentLicense)} - {formatLicenseSummary(licenseUsage)}
                  </Typography>
                  {nextLicense ? (
                    <Typography variant="caption" color="text.secondary">
                      Next license starts {nextLicense.valid_from}.
                    </Typography>
                  ) : null}
                </>
              ) : (
                <Alert severity="warning">
                  No active tenant license is configured. Activations and license-expanding role changes are blocked until a current license window exists.
                </Alert>
              )}

              {licenseAssessment.roleSeatUsage?.limit != null ? (
                <Typography variant="caption" color="text.secondary">
                  Target role {licenseAssessment.targetRole}: {licenseAssessment.roleSeatUsage.used}/{licenseAssessment.roleSeatUsage.limit} active seats used.
                </Typography>
              ) : null}

              {saveBlockedReason && !licenseLoading ? (
                <Alert severity="error">{saveBlockedReason}</Alert>
              ) : null}
            </Stack>
          </Paper>
        )
      ) : null}

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.25, md: 3 },
          borderRadius: 4,
          color: "#fff",
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 52%, ${theme.palette.info.light} 100%)`,
          boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.22)}`,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
        >
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{ color: "rgba(255,255,255,0.74)", letterSpacing: "0.14em", fontWeight: 800 }}
            >
              User Management
            </Typography>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-0.03em" }}>
                Edit {formData.name || "User"}
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.82)", mt: 0.65 }}>
                Update access, reporting, appraisal routing, and employee records from one workspace.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <SummaryPill label="Employee Code" value={formData.employee_code} />
              <SummaryPill label="System Role" value={formData.role || "employee"} />
              <SummaryPill label="Actual Role" value={formData.actual_role || DEFAULT_APPRAISAL_CATEGORY} />
              <SummaryPill label="Manager" value={formData.manager_id || "Unassigned"} />
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <Button
              variant="contained"
              type="button"
              startIcon={<SaveIcon />}
              onClick={saveAll}
              disabled={saveBlocked}
              sx={{
                backgroundColor: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.18)",
                boxShadow: "none",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.24)",
                  boxShadow: "none",
                },
              }}
            >
              Save Changes
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.38)",
                "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              Back
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={3} sx={{ borderRadius: 4, overflow: "hidden" }}>
        <Tabs value={tab} onChange={(_, newTab) => setTab(newTab)} variant="scrollable" scrollButtons="auto">
          <Tab label="Profile & Insurance" />
          <Tab label="Family" />
          <Tab label="Certificates" />
          <Tab label="Assets" />
        </Tabs>

        {/* TAB 0 */}
        {tab === 0 && (
          <Box component="form" onSubmit={saveAll} sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={3}>
              <FormSectionCard
                title="Access & Workflow"
                subtitle="Core account settings, system access, and the actual role used for appraisal routing."
              >
                <div className="row g-3">
              <div className="col-md-6">
                <TextField
                  fullWidth
                  required
                  name="employee_code"
                  label="Employee Code"
                  margin="normal"
                  value={formData.employee_code ?? ""}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  required
                  name="name"
                  label="Name"
                  margin="normal"
                  value={formData.name ?? ""}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  required
                  type="email"
                  name="email"
                  label="Email"
                  margin="normal"
                  value={formData.email ?? ""}
                  onChange={handleChange}
                />

                <TextField
                  select
                  fullWidth
                  name="role"
                  label="System Role"
                  margin="normal"
                  value={formData.role ?? "employee"}
                  onChange={handleChange}
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  name="actual_role"
                  label="Actual Role / Appraisal Category"
                  margin="normal"
                  value={formData.actual_role ?? DEFAULT_APPRAISAL_CATEGORY}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      actual_role: e.target.value,
                    }));
                  }}
                  helperText="This determines the default appraisal workflow unless a template override is selected."
                >
                  {actualRoleOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                {/* ✅ Workflow Template Override */}
                <TextField
                  select
                  fullWidth
                  name="performance_workflow_template_id"
                  label="Workflow Template Override (Optional)"
                  margin="normal"
                  value={formData.performance_workflow_template_id === "" ? "" : formData.performance_workflow_template_id}
                  onChange={handleChange}
                  helperText="If selected, this template will be used instead of category-based routing."
                >
                  <MenuItem value="">
                    <em>Auto (by Appraisal Category)</em>
                  </MenuItem>

                  {(workflowTemplates || []).map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name || `Template #${t.id}`}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  type="password"
                  name="password"
                  label="New Password (optional)"
                  helperText="Leave blank to keep current"
                  margin="normal"
                  value={formData.password ?? ""}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <Autocomplete
                  options={managerOptions}
                  getOptionLabel={(m) => `${m.employee_code || ""} - ${m.name || ""}`}
                  value={
                    managerOptions.find(
                      (m) => String(m.employee_code || "") === String(formData.manager_id || "")
                    ) || null
                  }
                  onChange={(_, val) =>
                    setFormData((prev) => ({
                      ...prev,
                      manager_id: val ? String(val.employee_code) : "",
                    }))
                  }
                  renderInput={(params) => <TextField {...params} margin="normal" label="Manager" />}
                />

                <TextField
                  fullWidth
                  name="position"
                  label="Position"
                  margin="normal"
                  value={formData.position ?? ""}
                  onChange={handleChange}
                />

                <TextField
                  select
                  fullWidth
                  name="department"
                  label="Department"
                  margin="normal"
                  value={formData.department ?? ""}
                  onChange={handleChange}
                  helperText="Linked to departments table (stores department code)."
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.code}>
                      {d.code ? `${d.code} - ${d.name}` : d.name || `Dept #${d.id}`}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  name="project_id"
                  label="Project"
                  margin="normal"
                  value={formData.project_id === "" ? "" : formData.project_id}
                  onChange={handleChange}
                  helperText="Primary project assignment (used for routing / reporting)."
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name || `Project #${p.id}`}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  name="cost_center"
                  label="Cost Center"
                  margin="normal"
                  value={formData.cost_center ?? ""}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  name="site"
                  label="Site"
                  margin="normal"
                  value={formData.site ?? ""}
                  onChange={handleChange}
                />
              </div>
                </div>
              </FormSectionCard>

              <FormSectionCard
                title="Documents & Contract"
                subtitle="Identity records, contract dates, and employee image uploads."
              >
                <div className="row g-3">
              <div className="col-md-6">
                <TextField
                  fullWidth
                  name="iqama_no"
                  label="Iqama No"
                  margin="normal"
                  value={formData.iqama_no ?? ""}
                  onChange={handleChange}
                />

                <TextField
                  fullWidth
                  name="passport_no"
                  label="Passport No"
                  margin="normal"
                  value={formData.passport_no ?? ""}
                  onChange={handleChange}
                />

                <TextField
                  fullWidth
                  name="passport_issue_date"
                  label="Passport Issued Date"
                  type="date"
                  margin="normal"
                  value={formData.passport_issue_date ?? ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  name="passport_expiry_date"
                  label="Passport Expiry Date"
                  type="date"
                  margin="normal"
                  value={formData.passport_expiry_date ?? ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              <div className="col-md-6">
                <TextField
                  fullWidth
                  name="nationality"
                  label="Nationality"
                  margin="normal"
                  value={formData.nationality ?? ""}
                  onChange={handleChange}
                />

                <TextField
                  fullWidth
                  name="birth_date"
                  label="Birth Date"
                  type="date"
                  margin="normal"
                  value={formData.birth_date ?? ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  name="contract_type"
                  label="Contract Type"
                  margin="normal"
                  value={formData.contract_type ?? ""}
                  onChange={handleChange}
                />

                <TextField
                  fullWidth
                  name="contract_issue_date"
                  label="Contract Issued Date"
                  type="date"
                  margin="normal"
                  value={formData.contract_issue_date ?? ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  name="contract_expiry_date"
                  label="Contract Expiry Date"
                  type="date"
                  margin="normal"
                  value={formData.contract_expiry_date ?? ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Images (Optional)
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", minWidth: 220 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Profile:
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={currentProfileImageUrl ? "Uploaded" : "None"}
                        color={currentProfileImageUrl ? "success" : "default"}
                      />
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: "none", fontWeight: 800 }}
                      >
                        {profileImageFile ? profileImageFile.name : "Upload"}
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={(e) => setProfileImageFile(e.target.files?.[0] || null)}
                        />
                      </Button>
                    </Box>

                    <Avatar
                      src={buildPublicFileUrl(currentProfileImageUrl) || undefined}
                      sx={{ width: 38, height: 38 }}
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Passport:
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={currentPassportImageUrl ? "Uploaded" : "None"}
                      color={currentPassportImageUrl ? "success" : "default"}
                    />
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: "none", fontWeight: 800 }}
                    >
                      {passportImageFile ? passportImageFile.name : "Upload"}
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={(e) => setPassportImageFile(e.target.files?.[0] || null)}
                      />
                    </Button>

                    {currentPassportImageUrl ? (
                      <Button
                        size="small"
                        variant="text"
                        href={buildPublicFileUrl(currentPassportImageUrl)}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ textTransform: "none", fontWeight: 800 }}
                      >
                        View current
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </div>
                </div>
              </FormSectionCard>

              <FormSectionCard
                title="Insurance"
                subtitle="Assigned policy, classification, and coverage dates."
              >
                <div className="row g-3">
              <div className="col-md-6">
                <TextField
                  select
                  fullWidth
                  name="insurance_policy_id"
                  label="Policy"
                  margin="normal"
                  value={formData.insurance_policy_id ?? ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      insurance_policy_id: e.target.value === "" ? "" : Number(e.target.value),
                      insurance_classification: "",
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {policies.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                  value={formData.start_date || ""}
                  onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))}
                />

                <TextField
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                  value={formData.end_date || ""}
                  onChange={(e) => setFormData((prev) => ({ ...prev, end_date: e.target.value }))}
                />

                {formData.insurance_policy_id && (
                  <TextField
                    select
                    fullWidth
                    name="insurance_classification"
                    label="Classification"
                    margin="normal"
                    value={formData.insurance_classification ?? ""}
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {(selPolicy?.classifications || []).map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </div>
                </div>
              </FormSectionCard>

              <StickySaveButton variant="contained" startIcon={<SaveIcon />} type="submit" disabled={saveBlocked}>
                Save Changes
              </StickySaveButton>
            </Stack>
          </Box>
        )}

        {/* TAB 1 */}
        {tab === 1 && (
          <Box component="form" onSubmit={saveAll} sx={{ p: 3 }}>
            <SectionTitle>Family Members</SectionTitle>

            <TextField
              size="small"
              placeholder="Search family…"
              value={familyFilter ?? ""}
              onChange={(e) => setFamilyFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {filteredFamily.length ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Relationship</TableCell>
                      <TableCell>Birth</TableCell>
                      <TableCell>Policy</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredFamily.map((fm) => {
                      const pol = policies.find((p) => p.id === fm.insurance_policy_id);
                      return (
                        <TableRow
                          key={fm.tempId || fm.id}
                          hover
                          sx={
                            fm._action === "create" || fm._action === "delete"
                              ? { backgroundColor: theme.palette.warning.light }
                              : {}
                          }
                        >
                          <TableCell>{fm.name}</TableCell>
                          <TableCell>{fm.relationship}</TableCell>
                          <TableCell>{fm.birth_date || "-"}</TableCell>
                          <TableCell>{pol ? pol.name : "-"}</TableCell>
                          <TableCell>{fm.insurance_classification || "-"}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleConfirmDeleteRelative(fm)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No family members.
              </Typography>
            )}

            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-3">
                <TextField
                  label="Name"
                  fullWidth
                  value={newFamilyMember.name ?? ""}
                  onChange={(e) => setNewFamilyMember((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <TextField
                  select
                  label="Relationship"
                  fullWidth
                  value={newFamilyMember.relationship ?? ""}
                  onChange={(e) => setNewFamilyMember((prev) => ({ ...prev, relationship: e.target.value }))}
                >
                  {relationshipOptions.map((rel) => (
                    <MenuItem key={rel} value={rel}>
                      {rel}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div className="col-md-3">
                <TextField
                  type="date"
                  label="Birth"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={newFamilyMember.birth_date ?? ""}
                  onChange={(e) => setNewFamilyMember((prev) => ({ ...prev, birth_date: e.target.value }))}
                />
              </div>

              <div className="col-md-2">
                <TextField
                  select
                  label="Policy"
                  fullWidth
                  value={newFamilyMember.insurance_policy_id ?? ""}
                  onChange={(e) =>
                    setNewFamilyMember((prev) => ({
                      ...prev,
                      insurance_policy_id: e.target.value,
                      insurance_classification: "",
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {policies.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              {newFamilyMember.insurance_policy_id && (
                <div className="col-md-2">
                  <TextField
                    select
                    label="Class"
                    fullWidth
                    value={newFamilyMember.insurance_classification ?? ""}
                    onChange={(e) =>
                      setNewFamilyMember((prev) => ({
                        ...prev,
                        insurance_classification: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {(selFamPolicy?.classifications || []).map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              )}

              <div className="col-md-1">
                <IconButton color="primary" onClick={handleAddRelative} aria-label="Add family member">
                  <AddIcon />
                </IconButton>
              </div>
            </div>

            <StickySaveButton variant="contained" startIcon={<SaveIcon />} type="submit" disabled={saveBlocked}>
              Save Changes
            </StickySaveButton>
          </Box>
        )}

        {/* TAB 2 */}
        {tab === 2 && (
          <Box component="form" onSubmit={saveAll} sx={{ p: 3 }}>
            <SectionTitle>Certificates</SectionTitle>

            <TextField
              size="small"
              placeholder="Search certificates…"
              value={certificateFilter ?? ""}
              onChange={(e) => setCertificateFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {filteredCertificates.length ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Issue Date</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCertificates.map((cert) => (
                      <TableRow
                        key={cert.tempId || cert.id}
                        hover
                        sx={
                          cert._action === "create" || cert._action === "delete"
                            ? { backgroundColor: theme.palette.warning.light }
                            : {}
                        }
                      >
                        <TableCell>{cert.title}</TableCell>
                        <TableCell>{cert.issue_date || "-"}</TableCell>
                        <TableCell>
                          {cert.certificate_file ? (
                            <Chip size="small" label={cert.certificate_file.name} color="info" variant="outlined" />
                          ) : cert.file_path ? (
                            <Button
                              size="small"
                              variant="text"
                              href={buildPublicFileUrl(cert.file_path)}
                              target="_blank"
                              rel="noreferrer"
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              View file
                            </Button>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {cert._action === "create" ? (
                            <Chip size="small" color="info" label="Pending create" />
                          ) : cert._action === "delete" ? (
                            <Chip size="small" color="warning" label="Pending delete" />
                          ) : (
                            <Chip size="small" color="success" label="Saved" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleConfirmDeleteCertificate(cert)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No certificates.
              </Typography>
            )}

            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-5">
                <TextField
                  label="Certificate Title"
                  fullWidth
                  value={newCertificate.title ?? ""}
                  onChange={(e) => setNewCertificate((prev) => ({ ...prev, title: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <TextField
                  type="date"
                  label="Issue Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={newCertificate.issue_date ?? ""}
                  onChange={(e) => setNewCertificate((prev) => ({ ...prev, issue_date: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <Button
                  component="label"
                  variant="outlined"
                  fullWidth
                  sx={{ textTransform: "none", fontWeight: 700, height: 56 }}
                >
                  {newCertificate.certificate_file ? newCertificate.certificate_file.name : "Upload File"}
                  <input
                    hidden
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      setNewCertificate((prev) => ({
                        ...prev,
                        certificate_file: e.target.files?.[0] || null,
                      }))
                    }
                  />
                </Button>
              </div>

              <div className="col-md-1">
                <IconButton color="primary" onClick={handleAddCertificate} aria-label="Add certificate">
                  <AddIcon />
                </IconButton>
              </div>
            </div>

            <StickySaveButton variant="contained" startIcon={<SaveIcon />} type="submit" disabled={saveBlocked}>
              Save Changes
            </StickySaveButton>
          </Box>
        )}

        {/* TAB 3 */}
        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <SectionTitle>Assets</SectionTitle>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle2">Assign Available Asset</Typography>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
                  <Autocomplete
                    fullWidth
                    options={availableAssets}
                    value={assetToAssign}
                    onChange={(_, value) => setAssetToAssign(value)}
                    getOptionLabel={(option) =>
                      option
                        ? `${option.asset_tag || "Asset"}${option.asset_type ? ` • ${option.asset_type}` : ""}${option.serial_number ? ` • ${option.serial_number}` : ""}`
                        : ""
                    }
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Available Assets" placeholder="Search asset tag or type" />
                    )}
                  />
                  <Button
                    variant="contained"
                    onClick={assignAsset}
                    disabled={!assetToAssign}
                    sx={{ minWidth: 140 }}
                  >
                    Assign Asset
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {userAssets.length ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Asset Tag</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Serial Number</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userAssets.map((asset) => (
                      <TableRow key={asset.id} hover>
                        <TableCell>{asset.asset_tag}</TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>{asset.asset_type || "-"}</TableCell>
                        <TableCell>{asset.serial_number || "-"}</TableCell>
                        <TableCell>
                          <StatusChip status={asset.status || "assigned"} />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            color="warning"
                            startIcon={<DeleteIcon />}
                            onClick={() => setConfirmAsset(asset)}
                          >
                            Return
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No assigned assets for this user.
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack("")} message={snack} />

      <Dialog open={!!confirmFm} onClose={() => setConfirmFm(null)}>
        <DialogTitle>Delete Family Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this family member?
            <br />
            (Changes finalize when “Save Changes” is clicked.)
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFm(null)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteRelative}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmCert} onClose={() => setConfirmCert(null)}>
        <DialogTitle>Delete Certificate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this certificate?
            <br />
            (Changes finalize when “Save Changes” is clicked.)
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCert(null)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteCertificate}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmAsset} onClose={() => setConfirmAsset(null)}>
        <DialogTitle>Return Asset</DialogTitle>
        <DialogContent>
          <DialogContentText>Mark asset "{confirmAsset?.asset_tag}" as returned?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAsset(null)}>Cancel</Button>
          <Button color="warning" onClick={returnAsset}>
            Return
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

